import { useEffect, useState } from 'react'
import { pressKitService, fetchDerivedStats } from '../services/pressKit.service'
import type { PressKitConfig } from '@/types'
import type {
  PressKitAssetWithId,
  PressKitReleaseWithId,
  PressKitCoverageWithId,
} from '../types'

export interface ResolvedStat {
  id: string
  label: string
  value: string
  order: number
}

interface UsePressKitResult {
  config: PressKitConfig | null
  stats: ResolvedStat[]
  assets: PressKitAssetWithId[]
  releases: PressKitReleaseWithId[]
  coverage: PressKitCoverageWithId[]
  loading: boolean
}

export function usePressKit(): UsePressKitResult {
  const [config, setConfig] = useState<PressKitConfig | null>(null)
  const [stats, setStats] = useState<ResolvedStat[]>([])
  const [assets, setAssets] = useState<PressKitAssetWithId[]>([])
  const [releases, setReleases] = useState<PressKitReleaseWithId[]>([])
  const [coverage, setCoverage] = useState<PressKitCoverageWithId[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function load() {
      const [cfg, rawStats, assetList, releaseList, coverageList] = await Promise.all([
        pressKitService.getConfig(),
        pressKitService.getVisibleStats(),
        pressKitService.getVisibleAssets(),
        pressKitService.getPublishedReleases(),
        pressKitService.getVisibleCoverage(),
      ])

      // If any stats are derived, pull the cached map once (refreshed via
      // Cloud Function if stale). Manual stats don't need it.
      const hasDerived = rawStats.some((s) => s.type === 'derived')
      const derived = hasDerived ? await fetchDerivedStats() : {}

      const resolved: ResolvedStat[] = rawStats.map((stat) => {
        const value =
          stat.type === 'derived' && stat.derivedKey
            ? derived[stat.derivedKey] ?? '0'
            : stat.value
        return { id: stat.id, label: stat.label, value, order: stat.order }
      })

      if (!cancelled) {
        setConfig(cfg)
        setStats(resolved)
        setAssets(assetList)
        setReleases(releaseList)
        setCoverage(coverageList)
        setLoading(false)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [])

  return { config, stats, assets, releases, coverage, loading }
}
